import React, { useEffect } from "react";
import { useSmartVideoPoster } from "../../utils/LazyVideo";
import videoLoadQueue from "../../utils/videoLoadQueue";
import { DATA_SRC_SET } from "../../utils/constants";

const THUMBNAIL_SEEK_TIMEOUT = 8000; // ms

const VideoCardLazy = ({ video, baseUrl, handleLoadedMetadata }: any) => {
    const { videoRef, isVisible, poster } = useSmartVideoPoster(video.filepath);

    useEffect(() => {
        if (!isVisible) {
            return;
        }
        const vid = videoRef?.current as HTMLVideoElement | null;
        if (!vid) {
            return;
        }

        // If the src is already set we don't need to enqueue again
        if (vid.getAttribute(DATA_SRC_SET)) {
            return;
        }

        const loader = async () => {
            return new Promise<void>((resolve) => {
                // If src is already set by some other flow, resolve
                if (vid.getAttribute(DATA_SRC_SET)) {
                    return resolve();
                }

                let timer = 0 as any;

                const cleanup = () => {
                    vid.removeEventListener('loadeddata', onLoaded);
                    vid.removeEventListener('error', onError);
                    if (timer) {
                        clearTimeout(timer);
                    }
                };

                const onLoaded = () => {
                    cleanup();
                    try { vid.setAttribute(DATA_SRC_SET, 'true'); } catch(e) {}
                    resolve();
                };

                const onError = () => {
                    // don't reject the queue on error; resolve so next items can proceed
                    cleanup();
                    try { vid.setAttribute(DATA_SRC_SET, 'true'); } catch(e) {}
                    resolve();
                };

                vid.addEventListener('loadeddata', onLoaded);
                vid.addEventListener('error', onError);

                // Ensure we request at least metadata
                try { vid.preload = 'metadata'; } catch (e) {}
                // set src to start loading/decoding (queue guarantees serialized decoding)
                try { vid.src = baseUrl + video.filepath; } catch (e) {}

                // Fallback: if loadeddata/err doesn't fire in time, resolve so we don't block forever
                timer = window.setTimeout(() => {
                    cleanup();
                    try { vid.setAttribute(DATA_SRC_SET, 'true'); } catch(e) {}
                    resolve();
                }, THUMBNAIL_SEEK_TIMEOUT);
            });
        };

        // enqueue the loader; queue serializes concurrent loads (default concurrency = 1)
        videoLoadQueue.enqueue(video.filepath, loader).catch(() => {});

    }, [isVisible, baseUrl, video.filepath, videoRef]);

    return (
        <div className={`videoPlayerContainer ${video.sharedBy ? 'sharedByAdminContainer' : ''}`}>
            <video
                ref={videoRef}
                src={/* leave empty - loader will set when visible */ ''}
                muted
                autoPlay={false}
                controls={false}
                poster={poster}
                loop
                playsInline
                crossOrigin="anonymous"
                className='videoPlayer'
                onLoadedMetadata={(event) => handleLoadedMetadata(event, video?.filepath, video?.coverImagePath, true)}
            />
        </div>
    );
};

export default VideoCardLazy;
